// Plausibility bands for the derived weight-series figures, and the two pure helpers the weight
// detail modal uses to keep one impossible day from destroying a chart's y-axis.
//
// Why this exists: `daily_gain_g` and `growth_index` are day-over-day derivations of the measured
// weight, so a mid-Production Group restock (weight legitimately drops, e.g. 13.83 kg → 6.29 kg) makes
// them briefly non-physical — observed as low as -1329 g/day and -1334.89 index on section 238.
// Highcharts auto-scales, so a single such point stretched the Growth Index axis to -2000…1000 and
// squashed the real 60–120 signal into a sliver.
//
// The fix is presentational only: out-of-band values are replaced with `null` (Highcharts draws a
// gap, so their absence is visible in the line as well as stated in the caveat next to the chart),
// never silently dropped or clamped to a fake in-band number. The underlying data problem is the
// backend's to fix.
//
// Pure — no Vue, no store, no i18n — so it stays cheap to reason about at the call site.

export type PlausibleRange = {
    min: number;
    max: number;
};

// A Growth Index is the section's growth as a percentage of its animal group's standard, with 100 as
// target. It cannot be negative (that would be shrinking faster than the standard grows, which the
// index is not defined for), and beyond double the standard it is a derivation artefact rather than a
// reading. The band is deliberately far wider than the ~0–150 the figure actually occupies, so only
// genuinely impossible values are affected.
export const GROWTH_INDEX_RANGE: PlausibleRange = { min: 0, max: 200 };

// Daily Gain in g/day. Unlike the index this legitimately goes negative — a section can lose weight
// over a day — so the floor is generous rather than zero. The ceiling is well above any real pig's
// daily gain; both bounds exist to catch restock-derived spikes in the ±1300 range.
export const DAILY_GAIN_G_RANGE: PlausibleRange = { min: -500, max: 2000 };

export function isPlausible(value: number | null, range: PlausibleRange): boolean {
    return value === null || (value >= range.min && value <= range.max);
}

// The value as it should be plotted: itself when plausible, `null` (a visible gap) when not.
export function plottableValue(value: number | null, range: PlausibleRange): number | null {
    return isPlausible(value, range) ? value : null;
}

// How many of the series' points `plottableValue` will blank out — drives the chart's caveat, so the
// user is told the points exist rather than left to read the gap as missing data. Points whose value
// is already null are not counted: nothing was withheld from the user there.
export function countImplausible<T>(points: T[], value: (point: T) => number | null, range: PlausibleRange): number {
    return points.reduce((count, point) => (isPlausible(value(point), range) ? count : count + 1), 0);
}
